import { FC, useMemo, useState } from "react"
import { ChevronDown, ClipboardCopy, Eye, MessageSquare } from "lucide-react"
import { openInNewTab } from "../../../../utils/downloadFile"

import BreadCrumbs from "../../../breadcrumbs"
import { LightBtn } from "../../../buttons"
import Dropdown from "../../../dropdown"
import BBSwitch from "../../../form/Switch"
import ProfileLogo from "../../../profile-logo"
import Select from "../../../form/select"
import If from "../../../if"
import Badge from "../../../badge"
import EngagementsStatus from "../../table/cells/engagements/engagements-status"
import {
  useAddThreadMutation,
  useGetThreadQuery
} from "../../../../../redux/queries"
import { pushToast } from "../../../../../redux/slices/notifications"
import { useAppDispatch, useInTabletView } from "../../../../hooks"
import { copyToClipboard, getStatusID } from "../../../../utils"
import { STATUS_OPTIONS } from "../../../../constants"
import {
  DropdownItemProps,
  LoanOfficer,
  SubscriptionDetails
} from "../../../../../types"

interface Props {
  data: SubscriptionDetails
  loDetails: LoanOfficer
  handleChangeRateAlerts: (subscription: SubscriptionDetails) => void
  onChangeStatus: (subscription: SubscriptionDetails, status: string) => void
}

const EngagementDetailsHeader: FC<Props> = ({
  data,
  loDetails,
  handleChangeRateAlerts,
  onChangeStatus
}) => {
  const inTabletView = useInTabletView()
  const dispatch = useAppDispatch()
  const [status, setStatus] = useState<string>(getStatusID(data))
  const selectedStatusOption = STATUS_OPTIONS.find(so => so.id === status)
  const {
    data: thread,
    isFetching: isFetchingThread,
    isError: isErrorThread
  } = useGetThreadQuery(
    { subscription_id: data.id },
    { skip: !data.id || !loDetails.sms_allowed }
  )
  const [
    addThread,
    { isLoading: isAddingThread, isError: isErrorAddingThread }
  ] = useAddThreadMutation()

  // Check if SMS has been sent
  const isSMSSent = useMemo(
    () => !!loDetails.sms_allowed && !isFetchingThread && !isErrorThread,
    [loDetails.sms_allowed, isFetchingThread, isErrorThread]
  )
  const showSendSMS = useMemo(
    () => !!loDetails.sms_allowed && !isFetchingThread && !isSMSSent,
    [loDetails.sms_allowed, isFetchingThread, isSMSSent]
  )

  const handleViewDashboard = () => openInNewTab(data.dashboard_link)
  const handleCopyDashboardLink = () => copyToClipboard(data.dashboard_link)
  const handleChangeStatus = (id: string) => {
    if (status === id) return
    setStatus(id)
    onChangeStatus(data, id)
  }
  const handleSendSMS = async () => {
    // dispatch the action to reply to the thread
    await addThread({ subscription_id: data.id })

    if (isErrorAddingThread) {
      dispatch(
        pushToast({
          msg: "Failed to send sms, please try again.",
          timeout: 3000,
          theme: "danger"
        })
      )
      return
    } else {
      dispatch(
        pushToast({
          msg: "SMS sent",
          timeout: 3000,
          theme: "success"
        })
      )
    }
  }

  const menuItems: DropdownItemProps[] = [
    {
      name: "Copy dashboard link",
      icon: ClipboardCopy,
      iconClassName: "text-neutral-400 mr-2",
      onClick: handleCopyDashboardLink
    }
  ]

  if (inTabletView) {
    const tabletItems: DropdownItemProps[] = [
      {
        renderItem: () => (
          <BBSwitch
            className="py-3 md:pb-0 md:px-4"
            enabled={data.active === "1"}
            setEnabled={() => handleChangeRateAlerts(data)}
            label="Rate alerts"
            switchPosition="left"
          />
        )
      },
      { type: "divider" }
    ]

    if (showSendSMS) {
      tabletItems.push({
        name: "Send SMS",
        icon: MessageSquare,
        iconClassName: "text-neutral-400 mr-2",
        onClick: handleSendSMS
      })
    }

    tabletItems.push({
      name: "View dashboard",
      icon: Eye,
      iconClassName: "text-neutral-400 mr-2",
      onClick: handleViewDashboard
    })

    menuItems.unshift(...tabletItems)
  }

  return (
    <>
      <BreadCrumbs
        pages={[
          {
            name: "Engagements",
            href: "/engagements",
            current: false
          },
          {
            name: data.email,
            href: "#",
            current: true
          }
        ]}
      />
      <div className="pt-6 pb-4 block sm:flex items-center justify-between">
        <div className="flex items-center">
          <ProfileLogo
            label={`${data.first_name.charAt(0)}${data.last_name.charAt(0)}`}
            className="w-16 h-16 shrink-0"
          />
          <div className="pl-4">
            <div className="flex items-center flex-wrap">
              <div className="font-bold text-xl mr-2">{data.email}</div>
              {<EngagementsStatus status={status} className="mr-2" />}
              <If
                condition={isSMSSent}
                then={
                  <>
                    <Badge.Alt className="py-px">SMS sent</Badge.Alt>
                    {thread?._id && (
                      <LightBtn
                        href={`/conversations/${thread._id}`}
                        className="ml-2 text-sm h-6 py-0 px-2"
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        View
                      </LightBtn>
                    )}
                  </>
                }
              />
            </div>
            <div className="text-base pt-1">
              {data.first_name} {data.last_name}
              <span className="text-gray-400">&nbsp;&bull;&nbsp;</span>
              {loDetails.bank}
            </div>
          </div>
        </div>
        <div className="flex items-center pl-0 mt-4 sm:mt-0">
          {inTabletView && <div className="w-16 pr-4"></div>}
          <BBSwitch
            className="hidden lg:flex"
            enabled={data.active === "1"}
            setEnabled={() => handleChangeRateAlerts(data)}
            label="Rate alerts"
          />
          <div className="ml-4">
            <Select
              label="Status"
              showLabel={false}
              selectClassName="w-[132px]"
              buttonClassName="h-12"
              selected={selectedStatusOption}
              data={STATUS_OPTIONS}
              IDkey="id"
              setSelected={handleChangeStatus}
              tooltipEnabled
            />
          </div>
          <If
            isLoading={isFetchingThread}
            loaderSize={4}
            condition={showSendSMS}
            then={
              <LightBtn
                type="button"
                className="hidden lg:inline-flex ml-4 text-sm md:text-base h-12"
                onClick={handleSendSMS}
              >
                <If
                  isLoading={isAddingThread}
                  loaderSize={4}
                  loaderClassName="!p-0"
                  condition={!isAddingThread}
                  then={"Send SMS"}
                />
              </LightBtn>
            }
          />
          <LightBtn
            className="hidden lg:inline-flex ml-4 text-sm md:text-base h-12"
            disabled={!data.dashboard_link}
            href={data.dashboard_link}
          >
            View dashboard
          </LightBtn>
          <Dropdown
            menuBtn={
              <div className="inline-flex justify-center items-center py-3 px-4 border text-sm md:text-base font-medium rounded-md focus:outline-none bg-neutral-100 h-12">
                Actions <ChevronDown className="h-5 w-5 ml-1" />
              </div>
            }
            items={menuItems}
            className="ml-4"
          />
        </div>
      </div>
    </>
  )
}

export default EngagementDetailsHeader
